/**************************************************/
/* Methods for preprocessing based on wasm code   */
/**************************************************/

import { ismatrix, Matrix  } from 'mdatools/arrays';
import { seq, alloc, allocCopy, isArrCorrect, checkedIndexCopy, wasmError } from './utils.js';

function floor(x) {
   return Math.floor(x);
}

function get_mpl_baseline(nx, mp) {

   if (mp[0] === 0) {
      const p = mp[1] + 2;
      return 2 + nx * p + 2 * nx + p; // EMSC: type (1), p (1), A (nx x p), lnorm (nx), x_ref(nx), c(p) - for memory
   }

   // ALS: type, lambda, p and place for baseline
   return 3 + nx;
}

// !!! order should match the one in C !!!
export const methodsList = [
   {id: 'norm',      title: 'Normalization',    mpl: 2, get_mpl_new: (nx, mp) => 2,                   mp: [0, 0]},
   {id: 'savgol',    title: 'Savitzky-Golay',   mpl: 3, get_mpl_new: (nx, mp) => mp[0] * mp[0],       mp: [3, 1, 0]},
   {id: 'scale',     title: 'Scaling',          mpl: 2, get_mpl_new: (nx, mp) => 2 * nx,              mp: [1, 0]},
   {id: 'baseline',  title: 'Baseline',         mpl: 3, get_mpl_new: get_mpl_baseline,                mp: [0, 0, 0]},
   {id: 'trim',      title: 'Trim tails',       mpl: 2, get_mpl_new: (nx, mp) => 2,                   mp: [0, 0]},
   // spikes: width (1), threshold (1), dx (nx), dev (nx), z (nx) and mp[0] values of extra space
   // for future use - 'prep_spikes' carves only the three nx arrays and never reads the tail. the
   // tail is slack rather than inert padding: 'mp[0]' is not validated here, so a negative stored
   // width SHRINKS the block instead. 'spikes_check' catches that, and where
   // trimming leaves the block long enough anyway, the 'width < 2' refusal in 'prep_spikes' does
   {id: 'spikes',    title: 'Spike removal',    mpl: 2, get_mpl_new: (nx, mp) => 2 + 3 * nx + mp[0],  mp: [5, 5]},
];


// a stored bound as C's 'stored_int' reads it: rounded half away from zero, or null when the
// rounded value is not finite or outside the int range
function storedInt(v) {
   const r = Math.sign(v) * Math.round(Math.abs(v));
   return Number.isFinite(r) && r >= -2147483648 && r <= 2147483647 ? r : null;
}

/**
 * Cuts the columns a preprocessing model trims off a matrix of preprocessed values.
 *
 * @param {Matrix} values matrix with preprocessed values (objects are rows, responses first).
 * @param {number} nresp number of responses.
 * @param {Object} prepmodel preprocessing model the values were preprocessed with.
 *
 * @returns {Array} the left and the right bound of the kept predictors and the cut matrix.
 *
 * The kept interval is the one C's 'prep_trim' reached when it applied the model, walked as
 * 'get_prepncols' walks it in C: a block shorter than two values is passed over, each bound is
 * read as 'stored_int' reads it and adopted only when it lies strictly inside the running
 * interval. Taken as they were with max/min, a pair on one value or crossed, which a build
 * before 051d4d7 could write, cut a zero or negative width here after C had applied the model.
 */
export function trim_values(values, nresp, prepmodel) {

   const trimNum = methodsList.findIndex(v => v.id === 'trim');

   let left = 0;
   let right = values.ncols - nresp;
   let offset = 0;
   for (let i = 0; i < prepmodel.ml.length; i++) {
      if (prepmodel.ml[i] === trimNum && prepmodel.mpl_new[i] >= 2) {
         const l = storedInt(prepmodel.mp_new[offset]);
         const r = storedInt(prepmodel.mp_new[offset + 1]);
         if (l !== null && r !== null) {
            if (l > left && l < right) left = l;
            if (r < right && r > left) right = r;
         }
      }
      offset += prepmodel.mpl_new[i];
   }

   const nrows = values.nrows;
   const npred_new = right - left;
   const ncols_new = npred_new + nresp;

   const new_values = Matrix.zeros(nrows, ncols_new);

   // the source ranges are views: 'set' copies each once into the new buffer, where a slice was
   // a second copy of each range. the source is a buffer of ours, never the wasm heap
   offset = 0;
   if (nresp > 0) {
      new_values.v.set(values.v.subarray(0, nresp * nrows), offset);
      offset += nresp * nrows;
   }

   // copy predictors
   new_values.v.set(values.v.subarray((nresp + left) * nrows, (nresp + right) * nrows), offset);

   return [left, right, new_values];
}

/**
 * Trims columns of preprocessed data if trimming is a part of the preprocessing model.
 *
 * @param {Object} d - JSON with dataset.
 * @param {Object} prepmodel - JSON with prepmodel.
 * @returns {Object} trimmed dataset.
 *
 * When preprocessing is applied to a matrix it does not remove values from trimmed tails to avoid
 * inconsistency (e.g. wrong varlabels or varvalues). This function does this, assuming that "d"
 * is dataset with already preprocessed data and "prepmodel" is preprocessing model used to create "d".
 * Returns the same object "d", changed in place when trimming applies.
 *
 */
export function prep_trim(d, prepmodel) {

   if (!d || !prepmodel || typeof prepmodel !== 'object' || Object.keys(prepmodel).length === 0) return d;


   // check if prepmodel contains trimming
   const trimNum = methodsList.findIndex(v => v.id === 'trim');
   const trimIndex = prepmodel.ml.findIndex(v => v === trimNum);
   if (trimIndex < 0) return d;

   const [left, right, new_values] = trim_values(d.values, d.nresp, prepmodel);


   d.values = new_values;
   d.varlabels = d.nresp > 0 ?  d.varlabels.slice(0, d.nresp).concat(d.varlabels.slice( d.nresp + left, d.nresp + right)) : d.varlabels.slice( left, right);
   d.varindices = d.nresp > 0 ? seq(0, d.nresp - 1).concat(seq(0, right - left - 1)) : seq(0, right - left - 1);
   d.varvalues = d.nresp > 0 ?  d.varvalues.slice(0, d.nresp).concat(d.varvalues.slice(d.nresp + left, d.nresp + right)) : d.varvalues.slice( left, right);

   return d;
}


/**
 * Creates a preprocessing model.
 *
 * @param {Object} Module object with wasm module for PCV.
 * @param {Matrix} D matrix with training set (predictors and responses).
 * @param {number} nresp number of responses.
 * @param {Array} prepobj array with preprocessing methods (method ID and its user defined parameters).
 * @param {boolean} transposed logical value, if true data has been already transposed (objects are columns),
 *
 * @returns object with preprocessing model.
 */
export function prep_fit(Module, D, nresp, prepobj, transposed = true, outliers = []) {

   if (!ismatrix(D)) {
      throw new Error('prep: D must be a matrix.');
   }

   if (!Array.isArray(prepobj) || prepobj.length < 1) {
      throw new Error('prep_fit: list with methods is not correct.');
   }

   let nrows, npred;
   if (transposed) {
      nrows = D.ncols;
      npred = D.nrows - nresp;
   } else {
      nrows = D.nrows;
      npred = D.ncols - nresp;
   }

   // this wrapper was the only one which passed the outlier indices on without looking at them.
   // the bound depends on the orientation, hence the check sitting after "nrows" is worked out
   if (!isArrCorrect(outliers, 0, nrows - 1)) {
      throw new Error('prep_fit: some indices in "outliers" array are outside the expected range.');
   }
   // a fractional index truncates on the way to C, so the row C drops is not the row the caller
   // named while the caller's labels still count it - a silent disagreement. the copy is given
   // its own name rather than assigned back over the parameter: 'pca_fit' and 'pca_predict'
   // return the caller's own list in their result and 'test-pca-wasm-exclvars' pins that by
   // identity, so the same discipline is kept in every wrapper. taken after 'isArrCorrect' has
   // refused any non-array; see 'checkedIndexCopy' for why the copy rather than the array
   const outlierValues = checkedIndexCopy(outliers,
      'prep_fit: indices in "outliers" array must be whole numbers.');

   const nmethods = prepobj.length;

   // compute length of vectors "mp" and "mp_new"
   // and set values for "ml", "mpl", and "mpl_new"

   const ml = new Int32Array(nmethods);
   const mpl = new Int32Array(nmethods);
   const mpl_new = new Int32Array(nmethods);

   let mp_length = 0;
   let mp_new_length = 0;

   for (let i = 0; i < prepobj.length; i++) {
      const id = prepobj[i].id;
      const params = prepobj[i].params;
      const ind = methodsList.findIndex(v => v.id == id);
      if (ind < 0) {
         throw new Error('prep_fit: wrong method name: ' + id + '.');
      }

      const method = methodsList[ind];
      if (params.length !== method.mpl) {
         throw new Error('prep_fit: wrong number of parameters for method: ' + id + '.');
      }

      ml[i] = ind;
      mpl[i] = method.mpl;
      mpl_new[i] = method.get_mpl_new(npred, params);
      mp_length = mp_length + mpl[i];
      mp_new_length = mp_new_length + mpl_new[i];
   }

   // set values for mp
   const mp = new Float64Array(mp_length);
   let offset = 0;
   for (let i = 0; i < prepobj.length; i++) {
      const params = prepobj[i].params;
      mp.set(params, offset);
      offset = offset + mpl[i];
   }


   // pointers and the result copy are hoisted so the 'finally' below can free every allocation no
   // matter where a throw happens, and so the copy outlives the block. a pointer which was never
   // assigned stays 0, and '_free(0)' is a no-op, so the frees below need no guards
   const nout = outliers.length;
   let ds_ptr = 0, ml_ptr = 0, mpl_ptr = 0, mpl_new_ptr = 0, mp_ptr = 0, mp_new_ptr = 0, out_ptr = 0;
   let mp_new_out;

   try {

      // allocate memory in WASM heap space and copy original values
      ds_ptr = allocCopy(Module, 'HEAPF64', D.v, 'data');

      // allocate memory in WASM heap space and values from methods list array
      ml_ptr = allocCopy(Module, 'HEAP32', ml, 'methods list');

      // allocate memory in WASM heap space and values from array with number of parameters for each method
      mpl_ptr = allocCopy(Module, 'HEAP32', mpl, 'number of parameters');

      // allocate memory in WASM heap space and values from array with number of adjusted parameters for each method
      mpl_new_ptr = allocCopy(Module, 'HEAP32', mpl_new, 'number of adjusted parameters');

      // allocate memory in WASM heap space and values from array with user defined parameters
      mp_ptr = allocCopy(Module, 'HEAPF64', mp, 'method parameters');

      // allocate memory in WASM heap space and values from array with methods parameters to be computed inside
      mp_new_ptr = alloc(Module, mp_new_length * mp.BYTES_PER_ELEMENT, 'adjusted method parameters');

      // '_malloc' hands back whatever the block last held, and a method which runs after a trim
      // fits only 'right - left' of its block, so the untouched tail of the scale, baseline and
      // spikes layouts is copied out below and lands in the saved model file. nothing reads it -
      // fit and apply agree on the same stride - but those are bytes of a previously processed
      // dataset in a file the user shares, and they make two fits of the same data differ.
      // it is done here and not in 'prepfit_wasm' because only this side knows the real
      // allocation: the entry point sees 'mpl_new', which a caller may declare larger than the
      // block it allocated, so zeroing there would write past it. the heap view is read after the
      // allocation because '_malloc' can grow the heap and replace 'Module.HEAPF64'
      const mp_new_start = mp_new_ptr / mp.BYTES_PER_ELEMENT;
      Module.HEAPF64.fill(0, mp_new_start, mp_new_start + mp_new_length);

      // allocate memory in WASM heap space for outliers
      if (nout > 0) {
         out_ptr = allocCopy(Module, 'HEAP32', new Int32Array(outlierValues), 'outliers');
      }

      // call WASM function for Preprocessing
      const mark = Module.errlines?.length ?? 0;
      const res = Module.ccall(
         'prepfit_wasm',
         [ 'number'], // return type
         [ 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'],
         [ds_ptr, nrows, npred, nresp, nmethods, ml_ptr, mpl_ptr, mp_ptr, mpl_new_ptr, mp_new_ptr, transposed, out_ptr, nout],
      );

      if (res !== 1) {
         throw wasmError(Module, 'prep_fit', mark);
      }

      // copy part of HEAP memory with the adjusted method parameters. this has to stay a 'slice' -
      // it makes an independent buffer, while a 'subarray' would be a view into memory the
      // 'finally' frees
      mp_new_out = Module.HEAPF64.slice(
         mp_new_ptr / mp.BYTES_PER_ELEMENT,
         mp_new_ptr / mp.BYTES_PER_ELEMENT + mp_new_length,
      );

   } finally {

      // freeing after a wasm trap is expected to be safe here: the instance survives an ordinary
      // trap and every pointer above is either 0 or valid. a trap caused by earlier heap corruption
      // could still leave the allocator damaged, in which case a throw from here would mask the
      // original error - that is accepted rather than guarded, since this build exports no abort flag
      Module._free(ds_ptr);
      Module._free(ml_ptr);
      Module._free(mp_ptr);
      Module._free(mpl_ptr);
      Module._free(out_ptr);
      Module._free(mp_new_ptr);
      Module._free(mpl_new_ptr);
   }

   // create preprocessing model object
   const model = {
      ml, mpl, mp, mpl_new, mp_new: mp_new_out, npred: npred, class: ['prepmodel']
   };

   // return the preprocessing model
   return model;
}


/**
 * Creates and applies a preprocessing model.
 *
 * @param {Object} Module object with wasm module for PCV.
 * @param {Matrix} D matrix with training set (predictors and responses).
 * @param {number} nresp number of responses.
 * @param {Object} model JSON with preprocessing model returned by 'prep_fit'.
 * @param {boolean} transposed logical value, if true data has been already transposed (objects are columns),
  *
 * @returns {Matrix} Matrix with preprocessed data.
 */
export function prep_apply(Module, D, nresp, model, transposed = true, trim = false) {

   if (!ismatrix(D)) {
      throw new Error('prep_apply: D must be a matrix.');
   }

   if (Object.keys(model).length === 0 || !model.ml) {
      return D;
   }

   let nrows, npred;
   if (transposed) {
      nrows = D.ncols;
      npred = D.nrows - nresp;
   } else {
      nrows = D.nrows;
      npred = D.ncols - nresp;
   }

   // check number of predictors
   if (npred !== model.npred) {
      throw new Error('prep_apply: D has wrong number of predictors (expected ' + model.npred + ' got ' + npred + ').');
   }

   // check the preprocessing model structure. the presence checks come before any '.reduce' or
   // '.length' - reducing first turned a model with no 'mpl_new' into a 'cannot read properties of
   // undefined' instead of the message below
   const ml = model.ml;
   const mpl_new = model.mpl_new;
   const mp_new = model.mp_new;
   if (!ml || !mpl_new || !mp_new) {
      throw new Error('prep_apply: wrong structure of the model object.');
   }

   // the kind of each array is checked, not just its length. a model read from a file can carry a
   // plain Array, which has no 'BYTES_PER_ELEMENT' at all, or a typed array of the wrong width -
   // and the copy used to take both the block size and the offset divisor from that width, so a
   // Float32Array landed at twice its own pointer instead of at it. 'allocCopy' now sizes and
   // offsets from the heap view, so such a model would no longer be written to the wrong address;
   // it is still rejected because these three arrays have one canonical form, and quietly widening
   // a Float32Array or truncating a Float64Array into 'HEAP32' would hide a wrong model rather
   // than report it
   if (!(ml instanceof Int32Array) || !(mpl_new instanceof Int32Array)) {
      throw new Error('prep_apply: model arrays "ml" and "mpl_new" must be Int32Array.');
   }

   if (!(mp_new instanceof Float64Array)) {
      throw new Error('prep_apply: model array "mp_new" must be Float64Array.');
   }

   const mp_new_length_exp = mpl_new.reduce((acc, v) => acc + v, 0);
   if (ml.length < 1 || mpl_new.length !== ml.length || mp_new.length !== mp_new_length_exp) {
      throw new Error('prep_apply: wrong structure of the model object.');
   }

   const nmethods = ml.length;

   // see the note in 'prep_fit' - pointers and the copy are hoisted so the 'finally' can free
   // unconditionally and the copy outlives the block
   let ds_ptr = 0, ml_ptr = 0, mpl_new_ptr = 0, mp_new_ptr = 0, dp_ptr = 0;
   let Dp;

   try {

      // allocate memory in WASM heap space and copy original values
      ds_ptr = allocCopy(Module, 'HEAPF64', D.v, 'data');

      // allocate memory in WASM heap space and values from methods list array
      ml_ptr = allocCopy(Module, 'HEAP32', ml, 'methods list');

      // allocate memory in WASM heap space and values from array with number of parameters for each method
      mpl_new_ptr = allocCopy(Module, 'HEAP32', mpl_new, 'number of adjusted parameters');

      // allocate memory in WASM heap space and values from array with user defined parameters
      mp_new_ptr = allocCopy(Module, 'HEAPF64', mp_new, 'adjusted method parameters');

      // allocate memory in WASM heap space for preprocessed data
      dp_ptr = alloc(Module, D.v.length * D.v.BYTES_PER_ELEMENT, 'preprocessed data');

      // call WASM function for Preprocessing
      const mark = Module.errlines?.length ?? 0;
      const res = Module.ccall(
         'prepapply_wasm',
         [ 'number'], // return type
         [ 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'],
         [ds_ptr, nrows, npred, nresp, nmethods, ml_ptr, mpl_new_ptr, mp_new_ptr, dp_ptr, transposed],
      );

      if (res !== 1) {
         throw wasmError(Module, 'prep_apply', mark);
      }

      // copy part of HEAP memory with preprocessed data - 'slice', not 'subarray', see 'prep_fit'
      Dp = Module.HEAPF64.slice(
         dp_ptr / D.v.BYTES_PER_ELEMENT,
         dp_ptr / D.v.BYTES_PER_ELEMENT + D.v.length,
      );

   } finally {
      Module._free(ds_ptr);
      Module._free(ml_ptr);
      Module._free(mpl_new_ptr);
      Module._free(mp_new_ptr);
      Module._free(dp_ptr);
   }

   // return preprocessed data as a matrix; 'trim_values' returns the bounds first, unused here
   const dp = new Matrix(Dp, D.nrows, D.ncols);
   if (trim) return trim_values(dp, nresp, model)[2];
   return dp;
}
